/**
 * DistributionChart — Bar wrapper over Recharts for funnels & distributions
 * (P0 kit). `orientation="horizontal"` (default) reads as a funnel; "vertical"
 * as a category distribution. Per-datum `tone` colors each bar from --g-*;
 * absent tone falls back to the primary series color. Wrapped in AdminStates;
 * `role="img"` + aria-label. Design: _shared/KIT-SPEC.md.
 */
import { type ReactElement } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AdminStates } from "@/features/admin/_shared/AdminStates";
import { CHART, toneColor, type ChartTone } from "@/features/admin/_shared/chartTokens";

export interface DistributionDatum {
  label: string;
  value: number;
  tone?: ChartTone;
}
export interface DistributionChartProps {
  data: readonly DistributionDatum[];
  orientation?: "vertical" | "horizontal";
  height?: number;
  isLoading?: boolean;
  isError?: boolean;
  loadingLabel: string;
  emptyLabel: string;
  errorLabel: string;
  valueFormatter?: (value: number) => string;
  ariaLabel: string;
  testid?: string;
}

const axisTick = { fill: CHART.axis, fontSize: 11 };

export function DistributionChart({
  data,
  orientation = "horizontal",
  height = 240,
  isLoading = false,
  isError = false,
  loadingLabel,
  emptyLabel,
  errorLabel,
  valueFormatter,
  ariaLabel,
  testid = "distribution-chart",
}: DistributionChartProps): ReactElement {
  const horizontal = orientation === "horizontal";
  const tooltipStyle = {
    background: CHART.surface,
    border: `1px solid ${CHART.grid}`,
    borderRadius: 10,
    color: CHART.ink,
    fontSize: 12,
  } as const;
  // Recharts' DefaultTooltipContent colors each item line with
  // `entry.color || '#000'` (see recharts/lib/component/DefaultTooltipContent.js)
  // when no `itemStyle` is supplied. `Bar` here carries no chart-level `fill`
  // (each `Cell` sets its own), so `entry.color` is undefined and Recharts
  // falls back to hardcoded black — invisible against the dark-theme tooltip
  // background (SCRUM-150). `contentStyle.color` only reaches the *label*
  // line via CSS inheritance, not the value line, so it must be set here too.
  const tooltipItemStyle = { color: CHART.ink } as const;

  return (
    <AdminStates
      isLoading={isLoading}
      isError={isError}
      isEmpty={data.length === 0}
      loadingLabel={loadingLabel}
      emptyLabel={emptyLabel}
      errorLabel={errorLabel}
      testid={testid}
    >
      <div data-testid={testid} role="img" aria-label={ariaLabel} style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data as DistributionDatum[]}
            layout={horizontal ? "vertical" : "horizontal"}
            margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
          >
            <CartesianGrid stroke={CHART.grid} horizontal={!horizontal} vertical={horizontal} />
            {horizontal ? (
              <>
                <XAxis type="number" tick={axisTick} stroke={CHART.grid} tickLine={false} tickFormatter={valueFormatter} />
                <YAxis type="category" dataKey="label" tick={axisTick} stroke={CHART.grid} tickLine={false} width={96} />
              </>
            ) : (
              <>
                <XAxis type="category" dataKey="label" tick={axisTick} stroke={CHART.grid} tickLine={false} />
                <YAxis type="number" tick={axisTick} stroke={CHART.grid} tickLine={false} width={36} tickFormatter={valueFormatter} />
              </>
            )}
            <Tooltip
              contentStyle={tooltipStyle}
              itemStyle={tooltipItemStyle}
              cursor={{ fill: CHART.grid, fillOpacity: 0.4 }}
            />
            <Bar dataKey="value" radius={horizontal ? [0, 6, 6, 0] : [6, 6, 0, 0]} isAnimationActive={false}>
              {data.map((d, i) => (
                <Cell key={i} fill={toneColor(d.tone ?? "primary")} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </AdminStates>
  );
}

export default DistributionChart;
